import { useReact19UniqueKeyCompatibility } from '@/hooks/use-react19-unique-key-compatibility';
import { LoadingDialog } from '@/providers/loading-dialogs/loading-dialog';
import LineBreak from '@/utils/line-break';
import {
    EnumFeature,
    EnumScreenName,
    EventProperties,
} from '@calm/evexia-analytics';
// eslint-disable-next-line no-restricted-imports -- raw token read kept until this site is moved to theme.semantics
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { CustomAxiosError } from '@calm/evexia-web-sdk';
import PageTitle from '@rhg/components/page-title';
import { PartnerActivationError } from '@rhg/components/partner-activation/error';
import { ActionsHeader } from '@rhg/components/ui/actions-header';
import { LayoutColumns, MaxColumnsContainer } from '@rhg/components/ui/layout';
import {
    JOIN_SPONSORSHIP_ERROR,
    PARTNER_ACTIVATION_ERROR,
    PARTNER_ACTIVATION_EXPIRED_ERROR,
} from '@rhg/hooks/registration/use-submit-flow';
import { useProvidedRegistrationFlowContext } from '@rhg/hooks/use-registration-context';
import {
    RegistrationFlow,
    RegistrationFlowContextState,
} from '@rhg/hooks/use-registration-context/types';
import registrationMessages from '@rhg/lang/messages/registration';
import { Box, Theme, SxProps, Stack } from '@rhg/libs/material';
import { styleColorOverlay, sxAppend } from '@rhg/libs/material/sx';
import { identifyEligibilityErrorType } from '@rhg/utils/eligibility';
import { isInvitationError } from '@rhg/utils/invitations';
import { Children, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import SsoSponsorshipError from '../sso-sponsorship-error';
import { RegistrationContainerErrorDialog } from './container-error-dialog';

const bottomActionContainerHeight = 73;

const styleContainer = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    position: 'relative',
};

const styleContentContainer = (hasActions: boolean) => ({
    flexGrow: 1,
    height: `calc(100% - ${hasActions ? bottomActionContainerHeight : 0}px)`,
    overflowY: 'auto',
    width: '100%',
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    py: 0,
    px: (theme: Theme) => ({
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
});
const styleActions: SxProps<Theme> = {
    position: 'sticky',
    height: bottomActionContainerHeight,
    bottom: 0,
    zIndex: (theme) => theme.zIndex.drawer,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    background: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? theme.palette.background.paper
            : styleColorOverlay(
                  DS_COLOR.DARK_BACKGROUND_PRIMARY_START,
                  DS_COLOR.BLACK_40P
              ).background,
    borderTop: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? `1px solid ${theme.palette.divider}`
            : 'none',
};
const styleButtons: SxProps<Theme> = {
    py: 3,
    mx: (theme: Theme) => ({
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
    alignItems: 'center',
};
const styleContent = (fullHeightContent: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    height: fullHeightContent ? '100%' : 'auto',
});

const errorStyleContainer = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
    height: '100%',
};

interface RegistrationFlowContainerHeaderProps {
    title?: string;
    /**
     * The document `<title>` for this step. Defaults to `title` so the
     * visible heading and the browser tab title stay in sync. Pass this
     * explicitly (instead of `title`) when a step already renders its own
     * visible `<h1>` and only needs the document title set.
     */
    documentTitle?: string;
    leftAction?: React.ReactNode;
    rightAction?: React.ReactNode;
}

interface RegistrationFlowContainerContext {
    context: Pick<
        RegistrationFlowContextState,
        | 'isPostEligibility'
        | 'isFetched'
        | 'newSponsor'
        | 'currentSponsorPartner'
        | 'submitIsPending'
        | 'submitFlowError'
        | 'submitFlowReset'
        | 'createCircleError'
        | 'createCircleReset'
        | 'registrationFlow'
        | 'inviteCodeState'
        | 'forcedError'
        | 'setForcedError'
        | 'trackStepNav'
    >;
}

interface RegistrationFlowContainerProps
    extends RegistrationFlowContainerHeaderProps {
    children: React.ReactNode;
    bottomActions: React.ReactNode;
    fullHeightContent?: boolean;
    screenName: EnumScreenName;
    featureName?: EnumFeature;
    trackProps?: EventProperties;
    sx?: SxProps<Theme>;
    contentColumns?: LayoutColumns;
    actionColumns?: LayoutColumns;
    icon?: React.ReactNode;
}

const styleIconContainer = {
    mb: (theme: Theme) => theme.ds.spacingUnits.BASE * 2,
    borderRadius: '50%',
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'dark' ? 'mask.black.20p' : 'background.paper',
    color: (theme: Theme) =>
        theme.palette.mode === 'dark' ? 'info.main' : 'primary.main',
    width: '64px',
    height: '64px',
};

export function RegistrationFlowContainerWithContext({
    fullHeightContent = false,
    children,
    title,
    documentTitle = title,
    leftAction,
    rightAction,
    bottomActions,
    sx,
    screenName,
    featureName = 'LoginRegistration',
    contentColumns = 6,
    actionColumns = 4,
    trackProps = {},
    context,
    icon,
}: RegistrationFlowContainerProps & RegistrationFlowContainerContext) {
    const {
        isFetched,
        isPostEligibility,
        newSponsor,
        currentSponsorPartner,
        submitFlowError,
        submitIsPending,
        createCircleError,
        registrationFlow,
        forcedError,
        trackStepNav,
        inviteCodeState: { hasInviteCode },
    } = context;
    const isJoinSponsorshipError =
        submitFlowError?.message === JOIN_SPONSORSHIP_ERROR;
    const isPartnerActivationExpiredError =
        submitFlowError?.message === PARTNER_ACTIVATION_EXPIRED_ERROR;
    const isPartnerActivationError =
        isPartnerActivationExpiredError ||
        submitFlowError?.message === PARTNER_ACTIVATION_ERROR;
    const { formatMessage } = useIntl();
    const { wrapNodesWithKeys } = useReact19UniqueKeyCompatibility();
    const hasTrackedPageNav = useRef(false);

    const hasDisplayableError = useMemo(
        () =>
            forcedError ||
            (submitFlowError &&
                !identifyEligibilityErrorType(
                    submitFlowError as CustomAxiosError
                )) ||
            (createCircleError &&
                isInvitationError(createCircleError as CustomAxiosError)),
        [forcedError, submitFlowError, createCircleError]
    );

    const trackedPartnerId = useMemo(() => {
        if (isPostEligibility) {
            return currentSponsorPartner?.id;
        } else {
            return newSponsor?.id;
        }
    }, [currentSponsorPartner?.id, isPostEligibility, newSponsor?.id]);

    useEffect(() => {
        if (trackStepNav && isFetched && !hasTrackedPageNav.current) {
            const additionalProps = {
                IsDependent: hasInviteCode, // We only determine the invitee registration flows after going through TOS
                ...(trackedPartnerId
                    ? {
                          PartnerID: trackedPartnerId,
                      }
                    : {}),
                ...trackProps,
            };
            hasTrackedPageNav.current = true;
            trackStepNav(screenName, featureName, undefined, additionalProps);
        }
    }, [
        featureName,
        hasInviteCode,
        isFetched,
        screenName,
        trackProps,
        trackStepNav,
        trackedPartnerId,
    ]);

    const hasBottomActions = Children.count(bottomActions) > 0;

    /*
     * Throw an error to contact support when the user is SSO and the join sponsorship call fails
     * This means the user has likely linked a new accout that is different than the existing one they have sponsorship with
     */
    if (isJoinSponsorshipError) {
        return (
            <Box sx={sxAppend(errorStyleContainer, sx)}>
                <SsoSponsorshipError />
            </Box>
        );
    }

    if (isPartnerActivationError) {
        return (
            <Box sx={sxAppend(errorStyleContainer, sx)}>
                <PartnerActivationError
                    variant={
                        isPartnerActivationExpiredError ? 'expired' : 'invalid'
                    }
                />
            </Box>
        );
    }

    if (submitIsPending) {
        if (registrationFlow === RegistrationFlow.LossOfSponsorship) {
            return (
                <Box sx={sxAppend(styleContainer, sx)}>
                    <LoadingDialog
                        isOpen
                        options={{
                            title: formatMessage(
                                registrationMessages[
                                    'registration.lost_sponsorship.convert_title'
                                ]
                            ),
                            subtitle: (
                                <>
                                    {wrapNodesWithKeys(
                                        formatMessage(
                                            registrationMessages[
                                                'registration.lost_sponsorship.convert_subtitle'
                                            ],
                                            {
                                                br: <LineBreak numLines={2} />,
                                            }
                                        )
                                    )}
                                </>
                            ),
                            background: 'content',
                            transitionDuration: 0,
                        }}
                    />
                </Box>
            );
        }

        if (
            [
                RegistrationFlow.ChangeOfSponsorship,
                RegistrationFlow.ChangeOfInviteeSponsorship,
                RegistrationFlow.UpgradeSponsorship,
            ].includes(registrationFlow)
        ) {
            return (
                <Box sx={sxAppend(styleContainer, sx)}>
                    <LoadingDialog
                        isOpen
                        options={{
                            title: formatMessage(
                                registrationMessages[
                                    'registration.change_sponsorship_interstitial.title'
                                ]
                            ),
                            subtitle: (
                                <>
                                    {wrapNodesWithKeys(
                                        formatMessage(
                                            registrationMessages[
                                                'registration.change_sponsorship_interstitial.subtitle'
                                            ],
                                            {
                                                br: <LineBreak numLines={2} />,
                                            }
                                        )
                                    )}
                                </>
                            ),
                            background: 'content',
                            transitionDuration: 0,
                        }}
                    />
                </Box>
            );
        }
    }

    return (
        <>
            {documentTitle && <PageTitle title={documentTitle} />}
            {hasDisplayableError && (
                <RegistrationContainerErrorDialog {...context} />
            )}
            <Box sx={sxAppend(styleContainer, sx)}>
                <Box sx={styleContentContainer(hasBottomActions)}>
                    <ActionsHeader
                        title={title}
                        leftAction={leftAction}
                        rightAction={rightAction}
                    />
                    <MaxColumnsContainer
                        columns={contentColumns}
                        sx={styleContent(fullHeightContent)}
                    >
                        {icon && (
                            <Stack
                                sx={styleIconContainer}
                                justifyContent="center"
                                alignItems="center"
                            >
                                {icon}
                            </Stack>
                        )}
                        {children}
                    </MaxColumnsContainer>
                </Box>
                {hasBottomActions ? (
                    <Box sx={styleActions}>
                        <MaxColumnsContainer
                            columns={actionColumns}
                            sx={styleButtons}
                        >
                            {bottomActions}
                        </MaxColumnsContainer>
                    </Box>
                ) : null}
            </Box>
        </>
    );
}

export const RegistrationFlowContainer = (
    props: RegistrationFlowContainerProps
) => {
    const context = useProvidedRegistrationFlowContext();
    return (
        <RegistrationFlowContainerWithContext {...props} context={context} />
    );
};
